import { Check, Copy, ExternalLink, Terminal } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CHATGPT_APP_URL } from '@/Domain/components/Integration/AccountMcpConnect/accountMcp.const';
import { SupportWhatsAppConnect } from '@/Domain/components/Integration/SupportWhatsApp/SupportWhatsAppConnect';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { TabItem, TabPanel, Tabs, TabsList } from '@/Domain/components/Shared/Tabs/Tabs';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { McpClaudeIcon } from '@/Domain/components/icons/McpClaude.icon';
import { McpCursorIcon } from '@/Domain/components/icons/McpCursor.icon';
import { OpenAiIcon } from '@/Domain/components/icons/OpenAi.icon';
import { WhatsAppIcon } from '@/Domain/components/icons/WhatsApp.icon';
import { AccountMcpClient, PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useCopiedFeedback } from '@/Domain/hooks/App/useCopiedFeedback.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useScrollEdgeFade } from '@/Domain/hooks/Shared/use-scroll-edge-fade';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';

// The hosted account MCP and its OAuth flow both live at app.quickchat.ai
// regardless of which dashboard origin the user is on, so the connect URL is
// the canonical production endpoint rather than window.location.origin.
const MCP_ENDPOINT = 'https://app.quickchat.ai/v1/api/mcp/rpc';
const DOCS_URL = 'https://docs.quickchat.ai/manage-with-ai';
const CLAUDE_CODE_COMMAND = `claude mcp add --transport http quickchat ${MCP_ENDPOINT}`;
const CODEX_COMMAND = `codex mcp add quickchat --url ${MCP_ENDPOINT}`;
const CURSOR_CONFIG = { url: MCP_ENDPOINT };
// Cursor one-click install deep link: base64-encoded per-server config.
const CURSOR_DEEPLINK = `cursor://anysphere.cursor-deeplink/mcp/install?name=quickchat&config=${btoa(
  JSON.stringify(CURSOR_CONFIG),
)}`;
const CURSOR_CONFIG_SNIPPET = JSON.stringify({ mcpServers: { quickchat: CURSOR_CONFIG } }, null, 2);
const VIDEO_EMBED_URL = 'https://www.youtube.com/embed/5JQYynBHfik?rel=0';

const CodeBlock = ({
  value,
  multiline = false,
  testId,
  onCopy,
}: {
  value: string;
  multiline?: boolean;
  testId?: string;
  onCopy?: () => void;
}) => {
  const { t } = useTranslation('common');
  const { copied, markCopied } = useCopiedFeedback();

  return (
    <div className="relative rounded-lg border border-border bg-muted">
      <pre
        className={`overflow-x-auto p-3 pr-12 font-mono text-xs leading-relaxed text-foreground ${
          multiline ? '' : 'whitespace-nowrap'
        }`}
      >
        <code>{value}</code>
      </pre>
      <button
        type="button"
        onClick={() => {
          if (copied) return;
          copyToClipboard(value, markCopied);
          onCopy?.();
        }}
        className="absolute right-2 top-2 flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label={t(copied ? 'copied' : 'copy')}
        data-testid={testId}
      >
        {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
        <span className="hidden sm:inline">{t(copied ? 'copied' : 'copy')}</span>
      </button>
    </div>
  );
};

const Steps = ({ items, start = 0 }: { items: ReactNode[]; start?: number }) => (
  <ol className="space-y-2.5">
    {items.map((item, index) => (
      <li key={index} className="flex gap-2.5">
        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-foreground">
          {start + index + 1}
        </span>
        <span className="pt-px text-sm text-muted-foreground">{item}</span>
      </li>
    ))}
  </ol>
);

export const AccountMcpConnectSheet = () => {
  const { t } = useTranslation('account_mcp');
  const { isOpen, closeModal, getParams } = useModalStore();
  const { capture } = usePostHogAnalytics();
  const open = isOpen(MODAL_NAMES.ACCOUNT_MCP_SHEET);
  const { ref: tabsScrollerRef, maskImage: tabsMaskImage } = useScrollEdgeFade();

  useEffect(() => {
    if (open) {
      capture(PostHogEventEnum.AccountMcpOpened, {
        source: getParams()?.source ?? 'unknown',
      });
    }
  }, [open, capture, getParams]);

  return (
    <Sheet open={open} onOpenChange={closeModal}>
      <SheetContent
        className="flex h-full w-full max-w-2xl flex-col gap-0 space-y-0 p-0"
        tabIndex={undefined}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          closeModal();
        }}
        data-testid="account-mcp-sheet"
      >
        <SheetHeader className="flex-row items-start justify-between gap-3 border-b border-border p-5">
          <div className="space-y-1">
            <SheetTitle className="text-base leading-tight">{t('title')}</SheetTitle>
            <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
          </div>
          <IconButton onClick={closeModal} variant="secondary" size="sm" className="shrink-0">
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">{t('endpoint_label')}</p>
            <CodeBlock
              value={MCP_ENDPOINT}
              testId="account-mcp-endpoint"
              onCopy={() =>
                capture(PostHogEventEnum.AccountMcpActionClicked, {
                  client: 'endpoint',
                  action: 'copy_endpoint',
                })
              }
            />
          </div>

          <Tabs
            defaultValue="chatgpt"
            onValueChange={(value) =>
              capture(PostHogEventEnum.AccountMcpTabSelected, {
                client: value as AccountMcpClient,
              })
            }
          >
            {/* Triggers never shrink below their label, so six of them push past the
                sheet edge on a phone. Same treatment as the Insights tab strip: scroll
                inside a wrapper, with the clipped edge faded so it reads as scrollable. */}
            <div
              ref={tabsScrollerRef}
              style={
                tabsMaskImage
                  ? { maskImage: tabsMaskImage, WebkitMaskImage: tabsMaskImage }
                  : undefined
              }
              className="max-w-full overflow-x-auto pb-1"
              data-testid="account-mcp-tabs-scroller"
            >
              <TabsList className="min-w-full">
                <TabItem value="chatgpt" className="flex-1 px-2">
                  <OpenAiIcon className="size-4" />
                  {t('tab.chatgpt')}
                </TabItem>
                <TabItem value="claude_desktop" className="flex-1 px-2">
                  <McpClaudeIcon className="size-4" />
                  {t('tab.claude_desktop')}
                </TabItem>
                <TabItem value="claude_code" className="flex-1 px-2">
                  <Terminal className="size-4" />
                  {t('tab.claude_code')}
                </TabItem>
                <TabItem value="cursor" className="flex-1 px-2">
                  <McpCursorIcon className="size-4" />
                  {t('tab.cursor')}
                </TabItem>
                <TabItem value="codex" className="flex-1 px-2">
                  <OpenAiIcon className="size-4" />
                  {t('tab.codex')}
                </TabItem>
                {/* fill overrides the icon's hardcoded brand green, which would not dim when inactive. */}
                <TabItem value="whatsapp" className="flex-1 px-2">
                  <WhatsAppIcon fill="currentColor" className="size-4" />
                  {t('tab.whatsapp')}
                </TabItem>
              </TabsList>
            </div>

            <TabPanel value="chatgpt" className="space-y-2.5 pt-4">
              <Steps items={[t('chatgpt.step_1')]} />
              <Button asChild variant="dark" size="sm" className="w-full">
                <a
                  href={CHATGPT_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    capture(PostHogEventEnum.AccountMcpActionClicked, {
                      client: 'chatgpt',
                      action: 'open_app',
                    })
                  }
                  data-testid="account-mcp-chatgpt-app"
                >
                  <OpenAiIcon className="size-4" />
                  {t('chatgpt.open_button')}
                </a>
              </Button>
              <Steps
                start={1}
                items={[
                  <Trans
                    key="chatgpt-connect"
                    t={t}
                    i18nKey="chatgpt.step_2"
                    components={[<strong key="connect" />]}
                  />,
                  t('chatgpt.step_3'),
                ]}
              />
            </TabPanel>

            <TabPanel value="claude_desktop" className="pt-4">
              <Steps
                items={[
                  t('claude_desktop.step_1'),
                  t('claude_desktop.step_2'),
                  t('claude_desktop.step_3'),
                  t('claude_desktop.step_4'),
                ]}
              />
            </TabPanel>

            <TabPanel value="claude_code" className="space-y-3 pt-4">
              <Steps items={[t('claude_code.step_1')]} />
              <CodeBlock
                value={CLAUDE_CODE_COMMAND}
                testId="account-mcp-claude-code-command"
                onCopy={() =>
                  capture(PostHogEventEnum.AccountMcpActionClicked, {
                    client: 'claude_code',
                    action: 'copy_command',
                  })
                }
              />
              <p className="text-sm text-muted-foreground">{t('claude_code.step_2')}</p>
            </TabPanel>

            <TabPanel value="cursor" className="space-y-3 pt-4">
              <Button asChild variant="dark" size="sm" className="w-full">
                <a
                  href={CURSOR_DEEPLINK}
                  onClick={() => {
                    capture(PostHogEventEnum.AccountMcpActionClicked, {
                      client: 'cursor',
                      action: 'open_deeplink',
                    });
                    toast(t('cursor.opening'));
                  }}
                  data-testid="account-mcp-cursor-deeplink"
                >
                  <McpCursorIcon className="size-4" />
                  {t('cursor.add_button')}
                </a>
              </Button>
              <p className="text-sm text-muted-foreground">{t('cursor.note')}</p>
              <CodeBlock
                value={CURSOR_CONFIG_SNIPPET}
                multiline
                onCopy={() =>
                  capture(PostHogEventEnum.AccountMcpActionClicked, {
                    client: 'cursor',
                    action: 'copy_config',
                  })
                }
              />
            </TabPanel>

            <TabPanel value="codex" className="space-y-4 pt-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">{t('codex.terminal_label')}</p>
                <p className="text-sm text-muted-foreground">{t('codex.step_1')}</p>
                <CodeBlock
                  value={CODEX_COMMAND}
                  testId="account-mcp-codex-command"
                  onCopy={() =>
                    capture(PostHogEventEnum.AccountMcpActionClicked, {
                      client: 'codex',
                      action: 'copy_command',
                    })
                  }
                />
                <p className="text-sm text-muted-foreground">{t('codex.step_2')}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">{t('codex.app_label')}</p>
                <Steps
                  items={[t('codex.app_step_1'), t('codex.app_step_2'), t('codex.app_step_3')]}
                />
              </div>
            </TabPanel>

            {/* Radix unmounts an inactive panel, so a code is minted on opening this tab rather than the sheet. */}
            <TabPanel value="whatsapp" className="pt-4">
              <SupportWhatsAppConnect enabled={open} />
            </TabPanel>
          </Tabs>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">{t('video_label')}</p>
            <iframe
              src={VIDEO_EMBED_URL}
              title={t('video_label')}
              className="aspect-video w-full rounded-lg border border-border"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              data-testid="account-mcp-video"
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <p className="text-sm font-medium text-foreground">{t('banner.title')}</p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t('banner.body')}</p>
          </div>
        </div>

        <div className="border-t border-border p-5">
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              capture(PostHogEventEnum.AccountMcpActionClicked, {
                client: 'docs',
                action: 'open_docs',
              })
            }
            className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:text-muted-foreground"
          >
            {t('docs')}
            <ExternalLink className="size-3.5" />
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
};
